import { useCallback } from "react";

import { useFormContext } from "react-hook-form";

import { signup, SignupBody } from "@/api/endpoints/signup";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg";
import { Email } from "@/containers/forms/user/fields/email";
import { Name } from "@/containers/forms/user/fields/name";
import { Password } from "@/containers/forms/user/fields/password";
import { ROUTES } from "@/routes_new";
import { serverErrorForHookForm } from "@/utils/forms";
import Button from "@components/Button";
import Typography from "@components/Typography";
import { useWizard } from "@components/Wizard";

import GoogleButton from "../GoogleButton";
import MicrosoftButton from "../MicrosoftButton";

import styles from "../../SignupView.module.css";

const PersonalInfo = () => {
  const { next } = useWizard();
  const { setError, getValues } = useFormContext<SignupBody>();

  const validate = useCallback(async () => {
    const values = getValues();
    return signup({ ...values, validate: true }).then((res) => {
      // Everything went fine, show the confirmation step
      if (res.status === 200) {
        next();
      }

      if (res && !res.ok && res.data) {
        // We don't want to validate the organization just now. So remove that from the error response.
        // so that the form can be submitted.
        if (res.data.organization_name) delete res.data.organization_name;
        const errors = serverErrorForHookForm<SignupBody>(res.data);
        errors.forEach(([str, error]) => setError(str, error));
        if (errors.length === 0) {
          next();
        }
      }
    });
  }, [next, setError, getValues]);

  return (
    <div className={styles.container}>
      <Typography variant="h1" block align="center" className={styles.title}>
        {L.SIGN_UP_PERSONAL_INFO_TITLE}
      </Typography>

      <Name autoFocus required />
      <Email required />

      <Password autoComplete="new-password" required />

      <Button
        className={styles.submit}
        type="submit"
        block
        iconPlacement="after"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          validate();
        }}
        icon={ArrowRightIcon}
      >
        {L.SIGN_UP_NEXT_STEP_CTA}
      </Button>
      <>
        <div className={styles.divider}>
          <Typography>{L.SIGN_UP_ALTERNATIVE_TITLE}</Typography>
        </div>
        <GoogleButton
          href={ROUTES.SIGNUP_SSO_GOOGLE}
          data-testid="signup-sso-google"
        >
          {L.LOGIN_CTA_GOOGLE}
        </GoogleButton>
        <MicrosoftButton
          href={ROUTES.SIGNUP_SSO_AZURE}
          data-testid="signup-sso-microsoft"
        >
          {L.LOGIN_CTA_MICROSOFT}
        </MicrosoftButton>
      </>
      <Typography
        className={styles.terms}
        align="center"
        margin="none"
        dangerouslySetInnerHTML={{ __html: L.SIGN_UP_TERMS }}
      />
    </div>
  );
};

export default PersonalInfo;
